import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Box, Switch, Typography, useTheme } from '@mui/material';
import { Button } from '@components/buttons/Button';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { BulletIcon } from '@icons/BulletIcon';
import { useTenantConfigDetail, useUpdateTenantConfig } from '@queryHooks/useTenantConfigs';
import { useGetSsoProviders } from '@queryHooks/useTenantSsoProvider';
import { SsoProviderCreateModal } from './SsoProviderCreateModal';
import { StatusBadge } from './SsoProviderStyles';
import { SsoProviderTable } from './SsoProviderTable';

export const SsoProviderPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const tenantId = params.tenantId as string;

  const {
    uiStore: { alertStore, toastStore },
  } = useStore();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data: ssoConfigValue } = useTenantConfigDetail(tenantId, 'ENABLE_TENANT_SSO', {
    enabled: !!tenantId,
  });
  const ssoEnabled = ssoConfigValue === 'true';

  const { data: providers, isLoading } = useGetSsoProviders(tenantId, {
    enabled: !!tenantId && ssoEnabled,
  });

  const { mutate: updateTenantConfig } = useUpdateTenantConfig();

  const handleToggleSso = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const actionLabel = checked ? t('SsoProvider.config.activate') : t('SsoProvider.config.deactivate');

    alertStore.open({
      title: actionLabel,
      message: checked ? t('SsoProvider.config.activateMessage') : t('SsoProvider.config.deactivateMessage'),
      useCancel: true,
      confirmName: actionLabel,
      onConfirm: () => {
        updateTenantConfig(
          {
            tenantId,
            configKey: 'ENABLE_TENANT_SSO',
            configValue: checked ? 'true' : 'false',
          },
          {
            onSuccess: () => {
              toastStore.open(
                checked ? t('SsoProvider.config.activateSuccess') : t('SsoProvider.config.deactivateSuccess'),
                'bottom',
                'center',
              );
            },
            onError: () => {
              toastStore.open(t('SsoProvider.config.fail'), 'bottom', 'center');
            },
          },
        );
      },
    });
  };

  return (
    <Container>
      <PageTitle>{t('SsoProvider.title')}</PageTitle>

      <ContentArea>
        <InfoBox>
          <InfoItem>
            <BulletIcon width={6} height={6} color={theme.colors['ic-gray-lighter']} />
            <InfoText>{t('SsoProvider.info')}</InfoText>
          </InfoItem>
        </InfoBox>

        <SsoToggleSection>
          <SsoToggleRow>
            <SsoToggleLabel>{t('SsoProvider.config.label')}</SsoToggleLabel>
            <SsoToggleRight>
              <Switch checked={ssoEnabled} onChange={handleToggleSso} />
              <StatusBadge className={ssoEnabled ? 'green' : 'red'}>
                {ssoEnabled ? t('SsoProvider.Status.active') : t('SsoProvider.Status.inactive')}
              </StatusBadge>
            </SsoToggleRight>
          </SsoToggleRow>
        </SsoToggleSection>

        {ssoEnabled && (
          <>
            <TableHeader>
              <Button variant="contain" size="small" paddingHorizontal={16} onClick={() => setIsCreateModalOpen(true)}>
                {t('SsoProvider.button.create')}
              </Button>
            </TableHeader>

            <SsoProviderTable providers={providers ?? []} isLoading={isLoading} />
          </>
        )}
      </ContentArea>

      <SsoProviderCreateModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        tenantId={tenantId}
      />
    </Container>
  );
};

const Container = styled(Box)`
  width: 100%;
  padding: 42px 32px 32px 32px;
  height: calc(100vh - var(--manager-gnb-height));
  overflow: auto;
`;

const PageTitle = styled(Typography)`
  ${fonts.Headline5}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  margin-bottom: 28px;
` as typeof Typography;

const ContentArea = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const InfoBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 20px 32px;
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
  border-radius: 3px;
`;

const InfoItem = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const InfoText = styled(Typography)`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
` as typeof Typography;

const SsoToggleSection = styled(Box)`
  padding: 15px 20px;
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 5px;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.02), 0px 0px 3px 0px rgba(0, 0, 0, 0.07);
`;

const SsoToggleRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const SsoToggleLabel = styled(Typography)`
  ${fonts.Headline7}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const SsoToggleRight = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const TableHeader = styled(Box)`
  display: flex;
  justify-content: flex-end;
`;
